import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ExclamationIcon,
  InformationCircleIcon,
  SparklesIcon,
  UserGroupIcon,
} from "@heroicons/react/outline";
import { TruncatedText } from "../../../common/components/TruncatedText/TruncatedText";

interface InteractionStat {
  contentId: number;
  contentName: string;
  successRate: number;
  attemptCount: number;
}

interface TopInteractionsCardProps {
  interactions: InteractionStat[];
  onInteractionClick?: (contentId: number, contentName: string) => void;
  isLoading?: boolean;
}

/**
 * Card showing top interactions quick insights and aggregated stats
 */
const TopInteractionsCard = ({
  interactions,
  onInteractionClick,
  isLoading,
}: TopInteractionsCardProps) => {
  const { t } = useTranslation();

  const insights = useMemo(() => {
    if (interactions.length === 0) return null;

    const totalInteractions = interactions.length;
    const totalAttempts = interactions.reduce((sum, i) => sum + i.attemptCount, 0);
    const avgSuccessRate =
      interactions.reduce((sum, i) => sum + i.successRate * i.attemptCount, 0) / totalAttempts;

    const sortedBySuccess = [...interactions].sort((a, b) => a.successRate - b.successRate);
    const worst = sortedBySuccess[0];
    const best = sortedBySuccess[sortedBySuccess.length - 1];

    const sortedByAttempts = [...interactions].sort((a, b) => b.attemptCount - a.attemptCount);
    const mostAttempted = sortedByAttempts[0];

    const problematic = interactions.filter((i) => i.successRate < 50);

    return {
      totalInteractions,
      totalAttempts,
      avgSuccessRate,
      problematicCount: problematic.length,
      best,
      worst,
      mostAttempted,
    };
  }, [interactions]);

  const getColor = (rate: number): { bg: string; text: string; icon: string } => {
    if (rate >= 80) return { bg: "bg-green-100", text: "text-green-700", icon: "text-green-500" };
    if (rate >= 50)
      return { bg: "bg-yellow-100", text: "text-yellow-700", icon: "text-yellow-500" };
    return { bg: "bg-red-100", text: "text-red-700", icon: "text-red-500" };
  };

  const renderInteractionRow = (
    interaction: InteractionStat,
    label: string,
    iconType: "best" | "worst" | "mostAttempted",
  ) => {
    const colors = getColor(interaction.successRate ?? 0);

    return (
      <button
        key={`${iconType}-${interaction.contentId}`}
        onClick={() => onInteractionClick?.(interaction.contentId, interaction.contentName)}
        className={`w-full flex items-center gap-3 p-2 rounded-lg ${colors.bg} hover:opacity-80 transition-opacity text-left`}
      >
        <div className={`flex-shrink-0 ${colors.icon}`}>
          {iconType === "best" && <SparklesIcon className="h-5 w-5" />}
          {iconType === "worst" && <ExclamationIcon className="h-5 w-5" />}
          {iconType === "mostAttempted" && <UserGroupIcon className="h-5 w-5" />}
        </div>

        <div className="flex-1 min-w-0">
          <TruncatedText
            as="p"
            className={`text-sm font-medium ${colors.text}`}
            text={interaction.contentName}
          />
          <p className="text-xs text-gray-500">
            {label}
            {iconType === "mostAttempted" && ` (${interaction.attemptCount})`}
          </p>
        </div>

        <div className={`flex-shrink-0 text-sm font-bold ${colors.text}`}>
          {(interaction.successRate ?? 0).toFixed(0)}%
        </div>
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm h-full">
        <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="space-y-2">
          <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm h-full flex flex-col">
      <h3 className="text-sm font-medium text-gray-500 mb-3">
        {t("statsNew.module.topInteractions")}
      </h3>

      {!insights ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          {t("statsNew.emptyState.noInteractions")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{insights.totalInteractions}</p>
              <p className="text-xs text-gray-500">
                {t("statsNew.interactions.totalInteractions")}
              </p>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{insights.totalAttempts}</p>
              <p className="text-xs text-gray-500">{t("statsNew.interactions.attempts")}</p>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg group relative">
              <p
                className={`text-lg font-bold ${
                  insights.avgSuccessRate >= 80
                    ? "text-green-600"
                    : insights.avgSuccessRate >= 50
                    ? "text-yellow-600"
                    : "text-red-600"
                }`}
              >
                {insights.avgSuccessRate.toFixed(0)}%
              </p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                {t("statsNew.charts.weightedAverage")}
                <InformationCircleIcon className="h-3 w-3 text-gray-400" />
              </p>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 w-48 text-center">
                {t("statsNew.charts.weightedAverageTooltip")}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            </div>
          </div>

          {insights.problematicCount > 0 && (
            <div className="flex items-center gap-2 p-2 mb-3 bg-red-50 border border-red-100 rounded-lg">
              <ExclamationIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-700">
                <span className="font-semibold">{insights.problematicCount}</span>{" "}
                {t("statsNew.interactions.problematic")}
              </p>
            </div>
          )}

          <div className="space-y-2 flex-1">
            {insights.worst &&
              renderInteractionRow(insights.worst, t("statsNew.module.lowestSuccess"), "worst")}

            {insights.mostAttempted &&
              insights.mostAttempted.contentId !== insights.worst?.contentId &&
              insights.mostAttempted.contentId !== insights.best?.contentId &&
              renderInteractionRow(
                insights.mostAttempted,
                t("statsNew.interactions.attempts"),
                "mostAttempted",
              )}

            {insights.best &&
              insights.best.successRate > 0 &&
              insights.best.contentId !== insights.worst?.contentId &&
              renderInteractionRow(insights.best, t("statsNew.module.highestSuccess"), "best")}
          </div>
        </>
      )}
    </div>
  );
};

export default TopInteractionsCard;
